import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../../../context/LoadingContext";
import { jwtDecode } from "jwt-decode";
import {
  FormControl,
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  fetchData,
  postQuery,
} from "../../Requirement/services/RequirementServices";
import AlertModal from "../../Requirement/components/common/AlerModel";

const EmailInformation = () => {
  const navigate = useNavigate();
  const { loading, setLoading } = useLoading();

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [activeUser, setActiveUser] = useState("");
  const [buyerInfo, setBuyerInfo] = useState({});
  const [secondaryEmail, setSecondaryEmail] = useState("");

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setLoading(true);

    const newErrors = {};
    if (!secondaryEmail) {
      newErrors.secondaryEmail = "Secondary email is required";
    } else if (!validateEmail(secondaryEmail)) {
      newErrors.secondaryEmail = "Invalid email format";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setLoading(false);
      return;
    }

    try {
      // Update secondary email API call
      const amtplUserID = buyerInfo.AMTPLUserID;

      const body = {
        target: "updateAMTPLUser",
        updateAMTPLUserInput: {
          AMTPLUserID: amtplUserID,
          SecondaryEmailID: secondaryEmail,
          EmailID: buyerInfo.EmailID,
          FirstName: buyerInfo.firstName || "",
          LastName: buyerInfo.lastName || "",
          Gender: buyerInfo.gender || "",
          Country: buyerInfo.country || "",
          State: buyerInfo.state || "",
          ZipCode: buyerInfo.pinZip || "",
          Phone1: buyerInfo.workingPhone || " ",
          Phone2: buyerInfo.officePhone || "",
          Address1: buyerInfo.address1 || " ",
          Address2: buyerInfo.address2 || " ",
        },
      };
      await postQuery(body);

      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: "Secondary email updated successfully.",
        autoClose: false,
      });
    } catch (error) {
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || "An error occurred. Please try again.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
      setSubmitted(false);
    }
  };

  const getBuyerInfo = useCallback(async (buyerEmail) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { EmailID: buyerEmail },
      });
      console.log("listAMTPLUsers::", response.items);
      const userData = response.items[0] || {};
      setBuyerInfo(userData);
      setSecondaryEmail(userData.SecondaryEmailID || "");
    } catch (error) {
      console.error("Error loading buyer info:", error);
      setBuyerInfo({});
      setSecondaryEmail("");
    }
  }, []);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_BUYERS")) {
          await Auth.signOut();
          navigate("/login/buyer");
        } else {
          setActiveUser(user.attributes.email);
          getBuyerInfo(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/buyer");
      }
    };
    checkUser();
  }, [navigate, getBuyerInfo]);

  return (
    <div className="container mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card p-4">
        <div className="table-header">
          <div className="header-text p-2">
            <div className="head-text">Email Settings</div>
            <div className="subtext" style={{marginBottom:"10px"}}>
              Manage your primary and secondary email addresses for
              notifications and account recovery.
            </div>
          </div>
          <div className="head-btn">
            <div className="submit-btn-form">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                form="questionForm"
                disabled={submitted || loading}
                onClick={handleSubmit}
              >
                {submitted || loading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </div>
        </div>
        <form id="questionForm" className="row g-2" onSubmit={handleSubmit}>
          <div className="row g-2">
            <div className="col-md-6 ">
              <FormControl fullWidth>
                <Typography variant="body2">Primary email address</Typography>
                <TextField
                  name="email"
                  size="small"
                  value={buyerInfo.EmailID || ""}
                  sx={{ backgroundColor: "white" }}
                  placeholder="Email"
                  inputProps={{ "aria-label": "Email", readOnly: true }}
                  disabled
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">Secondary email address</Typography>
                <TextField
                  name="secondaryEmail"
                  size="small"
                  value={secondaryEmail}
                  onChange={(e) => setSecondaryEmail(e.target.value)}
                  sx={{ backgroundColor: "white" }}
                  placeholder="Secondary Email"
                  inputProps={{ "aria-label": "Secondary Email" }}
                  error={!!errors.secondaryEmail}
                  helperText={errors.secondaryEmail}
                />
              </FormControl>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailInformation;
